import Game from "./core/game.js";
import AIHandler from "./ai/ai-handler.js";
import * as gsf from "./core/game-state-functions.js";
import * as random from "./core/util/random.js";
import * as coreFunctions from "./core/core-functions.js";
import * as constants from "./core/constants.js";
import * as shapeSpatialRelations from "./core/geometry/shape-spatial-relations.js";
import * as respawn from "./core/respawn.js";

export default function GameFactory() {
    // `startPhaseDuration` is the countdown proper; `rollCallDuration` (optional)
    // is prepended to it, so the whole start phase is roll call + countdown.
    function create({map, seed, players, disabledEffectTypes = [], disabledPowerUps = [], powerUpSpawnChance = constants.POWER_UP_SPAWN_CHANCE, wormSpeed = constants.WORM_SPEED, cheapAi = false, startPhaseDuration = constants.START_PHASE_DURATION, rollCallDuration = 0, crowdLevel = undefined, powerUpGreedValues = undefined, respawnRound = undefined}) {
        var startPhaseTotal = startPhaseDuration + rollCallDuration;
        var gameState = gsf.createGameState({map, seed, disabledEffectTypes, disabledPowerUps, powerUpSpawnChance, startPhaseTime: startPhaseTotal, rollCallTime: rollCallDuration, crowdLevel, powerUpGreedValues});

        function isTooCloseToOtherWorms(position, spacing) {
            for (var i in gameState.worms) {
                var worm = gameState.worms[i];
                if (shapeSpatialRelations.intersects(worm, {centerX: position.x, centerY: position.y, radius: constants.WORM_RADIUS + spacing})) {
                    return true;
                }
            }
            return false;
        }

        gsf.enterStartPhase(gameState, startPhaseTotal);

        map.blockingShapes.forEach(blockingShape => gsf.addPlayAreaObstacle(gameState, blockingShape));

        players.forEach(function (player) {
            gsf.addPlayer(gameState, {id: player.id});
            // Try to honour the full preferred spacing, but on a crowded arena
            // progressively relax it rather than failing, so any player count can
            // be seated (worms just start closer together). Spacing never drops
            // below WORM_RADIUS, so worms never spawn overlapping. This replaces an
            // old hard throw that crashed callers when too many worms were packed
            // onto a small map.
            var spacing = constants.START_DISTANCE_TO_WORMS;
            var position = coreFunctions.getRandomPositionInsidePlayableArea(gameState, constants.START_DISTANCE_TO_MAP + constants.WORM_RADIUS);
            var counter = 0;
            while (isTooCloseToOtherWorms(position, spacing)) {
                counter++;
                if (counter % 800 === 0 && spacing > constants.WORM_RADIUS) {
                    spacing = Math.max(constants.WORM_RADIUS, spacing * 0.8);
                }
                if (counter > 100000) {
                    break; // pathologically full: accept the last candidate instead of throwing
                }
                position = coreFunctions.getRandomPositionInsidePlayableArea(gameState, constants.START_DISTANCE_TO_MAP + constants.WORM_RADIUS);
            }

            gsf.addWorm(gameState, gsf.createWorm(gameState, {
                playerId: player.id,
                direction: random.random(gameState) * Math.PI * 2,
                centerX: position.x,
                centerY: position.y,
                radius: constants.WORM_RADIUS,
                speed: wormSpeed // undefined falls back to the default WORM_SPEED
            }));
        });

        // A Respawn round (core/respawn.js): `respawnRound` is { roundTime } or true
        if (respawnRound) {
            respawn.enable(gameState, Object.assign({ wormSpeed: wormSpeed }, respawnRound === true ? {} : respawnRound));
        }

        var aiHandler = AIHandler();
        players.filter(player => player.type === "bot")
            // `player.skill` (1-10, optional) carries through to the AI; undefined
            // keeps the original behaviour. `player.traits` (optional) is the
            // personality weight object (see docs/design/bots/PERSONALITIES.md).
            // `cheapAi` opts every bot into the cheap preview profile (used by
            // decorative lobby previews only).
            .forEach(player => aiHandler.addAIPlayer(gsf.getPlayer(gameState, player.id), player.skill, cheapAi, player.traits));

        return Game(gameState, aiHandler);
    }

    return {
        create: create
    };
};
